// src/features/entities/bulk-bar.tsx
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { TargetEnvRadio } from "@/features/data/target-env-radio";
import {
  useBulkEntities,
  useBulkGroups,
  useMergeEntities,
  useMergeGroups,
  usePropagateToMembers,
} from "@/hooks/use-data";
import { useTriggerRescrape } from "@/hooks/use-pipeline";
import type { TargetEnv } from "@/lib/types";
import { useState } from "react";
import { type BulkJobsCallback, DeleteSelectedDialog } from "./delete-selected-dialog";

interface BulkBarProps {
  selectedCount: number;
  selectedIds: string[];
  /**
   * Display names aligned 1:1 with selectedIds. Required for the new
   * Delete Selected dialog body + items-sweep banner-row labels. Caller
   * derives this from the same row source it draws selectedIds from.
   */
  selectedNames: string[];
  type: "groups" | "entities";
  onClear: () => void;
  /**
   * IR-661 Task 12: groups-side caller wires this to addJob so the
   * post-delete dual-env items-sweep banners appear automatically.
   * Entities path leaves it undefined (entities have no items-sweep).
   */
  onJobEnqueued?: BulkJobsCallback;
  /**
   * IR-1045 P2: scrapping_ids of the selected rows (groups only). When
   * present, a "Re-scrape" button triggers a pipeline re-scrape per id
   * (same action as the Monitoring tab). Rows without a scrapping_id are
   * omitted by the caller.
   */
  rescrapeIds?: string[];
}

export function BulkBar({
  selectedCount,
  selectedIds,
  selectedNames,
  type,
  onClear,
  onJobEnqueued,
  rescrapeIds,
}: BulkBarProps) {
  const bulkGroups = useBulkGroups();
  const bulkEntities = useBulkEntities();
  const mergeGroups = useMergeGroups();
  const mergeEntities = useMergeEntities();
  const propagate = usePropagateToMembers();
  const rescrape = useTriggerRescrape();

  const handleRescrape = () => {
    if (!rescrapeIds || rescrapeIds.length === 0) return;
    if (!confirm(`Re-scrape ${rescrapeIds.length} group(s)?`)) return;
    for (const sid of rescrapeIds) rescrape.mutate(sid);
    onClear();
  };

  const bulk = type === "groups" ? bulkGroups : bulkEntities;
  const merge = type === "groups" ? mergeGroups : mergeEntities;

  const [statusValue, setStatusValue] = useState("inactive");
  const [classValue, setClassValue] = useState("");
  const [riskValue, setRiskValue] = useState("");
  const [primaryIdx, setPrimaryIdx] = useState(0);
  // IR-661 Rotem reopen (2026-05-11): each mutating sub-dialog gets its
  // own env scope. Required, no default.
  const [statusEnv, setStatusEnv] = useState<TargetEnv | null>(null);
  const [classEnv, setClassEnv] = useState<TargetEnv | null>(null);

  const handleSetStatus = () => {
    if (statusEnv === null) return;
    bulk.mutate(
      {
        ids: selectedIds,
        action: "update_status",
        payload: { status: statusValue },
        targetEnv: statusEnv,
      },
      { onSuccess: onClear },
    );
  };

  const handleSetClassification = () => {
    if (classEnv === null) return;
    const payload: Record<string, unknown> = {};
    if (classValue) payload.classification = classValue;
    if (riskValue) payload.risk_score = Number(riskValue);
    bulk.mutate(
      {
        ids: selectedIds,
        action: "update_classification",
        payload,
        targetEnv: classEnv,
      },
      { onSuccess: onClear },
    );
  };

  const handleMerge = () => {
    const primary = selectedIds[primaryIdx];
    if (!primary) return;
    const mergeIds = selectedIds.filter((_, i) => i !== primaryIdx);
    merge.mutate({ primary_id: primary, merge_ids: mergeIds }, { onSuccess: onClear });
  };

  const handlePropagate = () => {
    propagate.mutate({ entity_ids: selectedIds, strategy: "highest_risk" }, { onSuccess: onClear });
  };

  return (
    <div className="flex items-center gap-3 rounded-lg border border-[var(--iris-neon-purple)]/30 bg-[var(--iris-neon-purple)]/5 px-4 py-2">
      <span className="text-xs font-medium text-[var(--iris-neon-purple)]">
        {selectedCount} selected
      </span>

      {/* Set Status */}
      <Dialog>
        <DialogTrigger asChild>
          <button type="button" className="iris-btn text-xs">
            Set Status
          </button>
        </DialogTrigger>
        <DialogContent>
          <DialogTitle>Set Status</DialogTitle>
          <DialogDescription>
            Update status for {selectedCount} {type}.
          </DialogDescription>
          <select
            className="iris-input mt-2 w-full"
            value={statusValue}
            onChange={(e) => setStatusValue(e.target.value)}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <div className="mt-2">
            <TargetEnvRadio value={statusEnv} onChange={setStatusEnv} actionVerb="update" />
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <DialogClose asChild>
              <button type="button" className="iris-btn text-xs">
                Cancel
              </button>
            </DialogClose>
            <DialogClose asChild>
              <button
                type="button"
                className="iris-btn iris-btn--primary text-xs"
                onClick={handleSetStatus}
                disabled={statusEnv === null}
              >
                Apply
              </button>
            </DialogClose>
          </div>
        </DialogContent>
      </Dialog>

      {/* Set Classification */}
      <Dialog>
        <DialogTrigger asChild>
          <button type="button" className="iris-btn text-xs">
            Set Classification
          </button>
        </DialogTrigger>
        <DialogContent>
          <DialogTitle>Set Classification</DialogTitle>
          <DialogDescription>
            Update classification for {selectedCount} {type}.
          </DialogDescription>
          <div className="mt-2 space-y-2">
            <input
              placeholder="Classification"
              className="iris-input w-full"
              value={classValue}
              onChange={(e) => setClassValue(e.target.value)}
            />
            <input
              placeholder="Risk Score"
              type="number"
              min={0}
              max={100}
              className="iris-input w-full"
              value={riskValue}
              onChange={(e) => setRiskValue(e.target.value)}
            />
          </div>
          <div className="mt-2">
            <TargetEnvRadio value={classEnv} onChange={setClassEnv} actionVerb="update" />
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <DialogClose asChild>
              <button type="button" className="iris-btn text-xs">
                Cancel
              </button>
            </DialogClose>
            <DialogClose asChild>
              <button
                type="button"
                className="iris-btn iris-btn--primary text-xs"
                onClick={handleSetClassification}
                disabled={classEnv === null}
              >
                Apply
              </button>
            </DialogClose>
          </div>
        </DialogContent>
      </Dialog>

      {/* Merge */}
      {selectedIds.length >= 2 && (
        <Dialog>
          <DialogTrigger asChild>
            <button type="button" className="iris-btn text-xs">
              Merge
            </button>
          </DialogTrigger>
          <DialogContent>
            <DialogTitle>Merge {type}</DialogTitle>
            <DialogDescription>
              Select the primary record. Others will be merged into it and soft-deleted.
            </DialogDescription>
            <div className="mt-2 space-y-1">
              {selectedIds.map((id, i) => (
                <label key={id} className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="primary"
                    checked={primaryIdx === i}
                    onChange={() => setPrimaryIdx(i)}
                    className="accent-[var(--iris-neon-purple)]"
                  />
                  <span className="font-mono text-xs text-[var(--iris-fg-3)]">
                    {id.slice(0, 12)}...
                  </span>
                  {i === primaryIdx && (
                    <span className="text-xs text-[var(--iris-neon-green)]">primary</span>
                  )}
                </label>
              ))}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <DialogClose asChild>
                <button type="button" className="iris-btn text-xs">
                  Cancel
                </button>
              </DialogClose>
              <DialogClose asChild>
                <button
                  type="button"
                  className="iris-btn text-xs"
                  style={{
                    background: "var(--iris-neon-pink)",
                    color: "var(--iris-bg-0)",
                    borderColor: "var(--iris-neon-pink)",
                  }}
                  onClick={handleMerge}
                >
                  Merge
                </button>
              </DialogClose>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Inherit from Groups (entities only) */}
      {type === "entities" && (
        <button type="button" className="iris-btn text-xs" onClick={handlePropagate}>
          Inherit from Groups
        </button>
      )}

      {/* IR-1045 P2: Re-scrape selected (groups only) — same pipeline trigger
          as the Monitoring tab. */}
      {type === "groups" && rescrapeIds && rescrapeIds.length > 0 && (
        <button
          type="button"
          className="iris-btn text-xs"
          onClick={handleRescrape}
          disabled={rescrape.isPending}
        >
          Re-scrape
        </button>
      )}

      {/* IR-661 Task 12: Delete Selected. Groups path fans out the
          dual-env cascade per row (Task 9); entities path is single-env
          soft-delete per row. Capped at 50/click in the dialog. */}
      <DeleteSelectedDialog
        selectedIds={selectedIds}
        selectedNames={selectedNames}
        type={type}
        onJobEnqueued={onJobEnqueued}
        onCleared={onClear}
      />

      <div className="ml-auto">
        <button type="button" className="iris-btn text-xs" onClick={onClear}>
          Clear
        </button>
      </div>
    </div>
  );
}
